import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { renderMd, fetchPackageText } from '../lib/markdown.js';
import OnboardingChecklist from '../components/OnboardingChecklist.jsx';
import { Rows, Field, Toggle, WEEKDAYS, TIMEZONES, splitLines, fmtDate, fmtDateTime } from '../components/FormBits.jsx';
import { ApprovalFields, approvalFromPack, approvalPayload } from '../components/ApprovalBits.jsx';
import Working from '../components/Working.jsx';
import { GmbTab } from './AdminGmb.jsx';
import { SeoTasksTab } from './AdminSeoTasks.jsx';
import { clientPath, useFollowSwitcher } from '../lib/clientLink.js';
import { isDemoName, nonAgencyEmails, badDemoSignins, AGENCY_DOMAIN, DEMO_SIGNIN_RULE } from '../lib/demo.js';   // Session 67: demo clients; Session 68b: the sign-in rule
import DemoPanel from '../components/DemoPanel.jsx';   // Session 68: the demo walkthrough (autopilot + Reset demo)
import { CadenceTypeChoice, MonthlyFields, MONTHLY_DEFAULTS, minLeadDays } from '../components/CadenceBits.jsx';
import PublishingTab from '../components/PublishingTab.jsx';
import { SaveStatus, useSaveStatus } from '../components/SaveStatus.jsx';
import { courseMode, seoRecsMode } from '../modules/index.js';
import { SEO_RECS_MODES, SEO_RECS_HINT, COURSE_MODES, COURSE_HINT, BLOG_FOCUS } from '../components/ServiceBits.jsx';   // Session 55 Part C / 57: shared with Add client
// Wave 13 (session 46): the newsletter settings, the same form the client sees in onboarding.
import NewsletterSetup, { newsletterFromPack, newsletterPayload } from '../components/NewsletterSetup.jsx';
// Session 63: the title images we make (look, painter) and the brand they are drawn in.
import BrandSetup, { ImagesSetup, brandFromPack, brandPayload, imagesFromPack } from '../components/HeroLooks.jsx';

// Session 6 Part B: agency-only client settings.
//   Onboarding  persistent checklist (v_portal_onboarding)
//   Analysis    every baseline run, dated, viewable; re-run with notes; uploads
//   Edit client everything from the onboarding form, saved through
//               portal_update_client; cadence changes preview first
//   GMB Audit   Session 11: upload, review and release (AdminGmb.jsx)
//   Publishing  Session 12B: Build A Better Website Partner API (PublishingTab.jsx)

const TABS = [['onboarding', 'Onboarding'], ['analysis', 'Analysis'], ['edit', 'Edit client'], ['publishing', 'Publishing'], ['gmb', 'GMB Audit'], ['seo', 'SEO checklist']];

export default function AdminClientSettings() {
  const { clientId } = useParams();
  const [params, setParams] = useSearchParams();
  const { isAgency, setActiveId, activeId, reloadClients, session } = useAuth();
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'onboarding';

  const [client, setClient] = useState(null);
  const [onb, setOnb] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const [{ data: c, error: e1 }, { data: o, error: e2 }] = await Promise.all([
      supabase.from('v_portal_client_settings').select('*').eq('client_id', clientId).maybeSingle(),
      supabase.from('v_portal_onboarding').select('*').eq('client_id', clientId).maybeSingle(),
    ]);
    if (e1 || e2) { setError((e1 || e2).message); return; }
    setClient(c || false); setOnb(o || null);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (clientId && clientId !== activeId) setActiveId(clientId); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Session 17: a switcher pick opens the same tab for the picked client.
  useFollowSwitcher(clientId, (id) => `/admin/clients/${id}/settings?tab=${tab}`);

  if (!isAgency) return <Navigate to="/" replace />;
  if (error) return <div className="empty"><h2>Couldn't load the client</h2><p>{error}</p></div>;
  if (client === null) return <p className="lede">Loading…</p>;
  if (client === false) return <div className="empty"><h2>Client not found</h2></div>;

  return (
    <>
      {/* Session 24: the client's name, large, so it's clear whose settings these are */}
      <div className="settings-client-name">{client.name}</div>
      <h1>Settings</h1>
      <p className="lede">
        Where this client stands, every analysis we've run, everything we know about them, and their Google Business Profile audit.
        {' '}<Link to={clientPath(`/admin/clients/${clientId}/adhoc/new`, clientId)}>New ad hoc blog →</Link>
      </p>
      <div className="tabs" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k}
                  className={`tab ${tab === k ? 'active' : ''}`}
                  onClick={() => setParams({ tab: k, client: clientId })}>{label}</button>
        ))}
      </div>
      {tab === 'onboarding' && <OnboardingTab clientId={clientId} row={onb} />}
      {tab === 'analysis'   && <AnalysisTab clientId={clientId} client={client} onChange={load} />}
      {tab === 'edit'       && <EditTab client={client} onSaved={async () => { await load(); await reloadClients(); }} />}
      {tab === 'publishing' && <PublishingTab client={client} onSaved={async () => { await load(); await reloadClients(); }} />}
      {tab === 'gmb'        && <GmbTab clientId={clientId} clientName={client.name} />}
      {tab === 'seo'        && <SeoTasksTab clientId={clientId} clientName={client.name} pack={client.pack}
                                            onPackChange={async () => { await load(); await reloadClients(); }} />}
    </>
  );
}

// ---------------------------------------------------------------------------

function OnboardingTab({ clientId, row }) {
  if (!row) return <p className="lede">Loading…</p>;
  return (
    <section className="review-section">
      <h2>Onboarding checklist</h2>
      <p className="hint" style={{ marginTop: 0, marginBottom: 14 }}>
        Each step is read from the records, so this list is the same whenever you come back.
        Anything still open can be moved along from the <Link to={clientPath(`/admin/clients/${clientId}/onboarding`, clientId)}>onboarding page</Link>.
      </p>
      <OnboardingChecklist row={row} />
    </section>
  );
}

// ---------------------------------------------------------------------------

function runLabel(job, idx) {
  if (idx === 0) return 'Initial analysis';
  return `Re-run ${fmtDate(job.created_at)}`;
}
function runState(status) {
  return { approved: ['Approved', 'ok'], flagged: ['Superseded', 'muted'], agency_review: ['Pending review', 'wait'],
           failed: ['Failed', 'err'], queued: ['Running', 'wait'], running: ['Running', 'wait'] }[status] || [status, 'muted'];
}

function AnalysisTab({ clientId, client, onChange }) {
  const [runs, setRuns] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [viewing, setViewing] = useState(null);       // job row
  const [docs, setDocs] = useState({ baseline: '', strategy: '' });
  const [docTab, setDocTab] = useState('baseline');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('v_portal_baselines').select('*').eq('client_id', clientId).order('created_at', { ascending: true });
    if (error) { setBanner({ tone: 'err', text: error.message }); return; }
    setRuns(data || []);
    const { data: files } = await supabase.storage.from('post-packages').list(`${clientId}/analysis/uploads`, { limit: 100 });
    setUploads((files || []).filter((f) => f.name && !f.name.startsWith('.')));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  async function view(job) {
    setViewing(job); setDocs({ baseline: '_Loading…_', strategy: '_Loading…_' }); setDocTab('baseline');
    const strategyPath = job.strategy_path || null;
    const [b, s] = await Promise.all([
      job.baseline_path ? fetchPackageText(supabase, job.baseline_path).catch((e) => `_Couldn't load: ${e.message}_`) : Promise.resolve('_No findings file for this run._'),
      strategyPath ? fetchPackageText(supabase, strategyPath).catch((e) => `_Couldn't load: ${e.message}_`) : Promise.resolve('_No strategy preview for this run._'),
    ]);
    setDocs({ baseline: b, strategy: s });
  }

  async function rerun() {
    setBusy(true); setBanner(null);
    const { error } = await supabase.rpc('portal_request_analysis', { p_client_id: clientId, p_notes: notes.trim() || null });
    setBusy(false);
    if (error) { setBanner({ tone: 'err', text: error.message }); return; }
    setNotes('');
    setBanner({ tone: 'ok', text: 'Analysis queued. It usually takes 10 to 20 minutes; you\'ll get an email when it\'s ready.' });
    load(); onChange();
  }

  const running = (runs || []).some((r) => ['queued', 'running'].includes(r.status));
  const approvedId = client.pack?.baseline?.job_id;

  // Fix A: while an analysis runs, show it and refresh the list until it lands.
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [running, load]);

  return (
    <>
      <section className="review-section">
        <h2>Analysis archive</h2>
        <p className="hint" style={{ marginTop: 0 }}>Every run is kept. Nothing here is ever deleted.</p>
        {running && <Working text="Analysing the site. This usually takes 10 to 20 minutes; the list updates on its own." />}
        {runs === null && <p className="lede">Loading…</p>}
        {runs && runs.length === 0 && <p>No analysis has been run yet.</p>}
        {runs && runs.length > 0 && (
          <div className="card">
            {runs.map((r, i) => {
              const [label, tone] = runState(r.status);
              return (
                <div className="run-row" key={r.job_id}>
                  <div className="run-main">
                    <div className="run-title">{runLabel(r, i)}{r.job_id === approvedId && <span className="required-pill">live strategy</span>}</div>
                    <div className="run-sub">
                      {fmtDateTime(r.created_at)}
                      {r.notes ? <> · notes: “{r.notes}”</> : null}
                      {r.upload_path ? <> · used upload {String(r.upload_path).split('/').pop()}</> : null}
                      {r.error ? <> · {r.error}</> : null}
                    </div>
                  </div>
                  <span className={`chip chip-run-${tone}`}>{label}</span>
                  <div className="run-actions">
                    {r.baseline_path && <button type="button" className="link-btn" onClick={() => view(r)}>View</button>}
                    {r.status === 'agency_review' && <Link className="btn btn-primary" to={clientPath(`/admin/clients/${clientId}/analysis`, clientId)}>Review</Link>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {uploads.length > 0 || client.pack?.analysis_upload ? (
          <>
            <h3 style={{ marginTop: 18 }}>Uploaded analyses</h3>
            <ul className="upload-list">
              {uploads.map((f) => <li key={f.name}>{f.name}{client.pack?.analysis_upload?.path?.endsWith(f.name) ? ' (used as the main source)' : ''}</li>)}
              {uploads.length === 0 && client.pack?.analysis_upload?.path && <li>{client.pack.analysis_upload.path.split('/').pop()}</li>}
            </ul>
          </>
        ) : null}
      </section>

      {viewing && (
        <section className="review-section">
          <div className="section-heading" style={{ margin: '0 0 10px' }}>
            <h2>{runLabel(viewing, (runs || []).findIndex((r) => r.job_id === viewing.job_id))}</h2>
            <button type="button" className="link-btn" onClick={() => setViewing(null)}>Close</button>
          </div>
          <div className="review-actions" style={{ justifyContent: 'flex-start', marginTop: 0 }} role="tablist">
            <button type="button" className={`btn ${docTab === 'baseline' ? 'btn-primary' : ''}`} onClick={() => setDocTab('baseline')}>Full findings</button>
            <button type="button" className={`btn ${docTab === 'strategy' ? 'btn-primary' : ''}`} onClick={() => setDocTab('strategy')}>Strategy preview</button>
          </div>
          <article className="md-doc strategy-doc" style={{ marginTop: 14 }}
                   dangerouslySetInnerHTML={{ __html: renderMd(docTab === 'baseline' ? docs.baseline : docs.strategy) }} />
        </section>
      )}

      <section className="review-section">
        <h2>Re-run analysis</h2>
        {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}
        <Field label="Notes for this run" hint="Optional. What should the next run do differently, or what changed on the site?">
          <textarea className="review-textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={busy || running} />
        </Field>
        <div className="review-actions">
          <button type="button" className="btn btn-primary" disabled={busy || running} onClick={rerun}>
            {running ? 'An analysis is running…' : 'Re-run analysis'}
          </button>
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------

const DAY_INDEX = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

function initialForm(client) {
  const p = client.pack || {};
  const cad = p.cadence || {};
  const by = p.people?.byline || {};
  return {
    name: client.name || '',
    publish_adapter: client.publish_adapter || 'handoff',
    business: {
      website: p.business?.website || '',
      description: p.business?.description || '',
      service_area: p.business?.service_area || '',
      industry: p.business?.industry || '',
      services: (p.business?.services || []).map((s) => ({ url: s.url || '', description: s.description || '' })),
    },
    market: {
      competitors: (p.market?.competitors || []).map((c) => ({ name: c.name || '', url: c.url || '' })),
      avoid_topics: p.market?.avoid_topics || '',
      proof: (p.market?.proof || []).map((x) => ({ text: x.text || '', url: x.url || '' })),
    },
    voice: {
      tone: p.voice?.tone || '',
      use_phrases: (p.voice?.use_phrases || []).join('\n'),
      banned_phrases: (p.voice?.banned_phrases || []).join('\n'),
    },
    people: {
      notice_emails: (p.people?.notice_emails || []).join('\n'),
      signin_emails: (p.people?.signin_emails || []).join('\n'),
      approver_email: p.people?.approver_email || '',
      byline: { name: by.name || '', title: by.title || '', bio: by.bio || '', url: by.url || '', org: by.org || '' },
    },
    // Session 12 Part A: pack.analytics replaces the bare gsc_property field
    // (the RPC still mirrors the GSC site into pack.gsc_property).
    analytics: {
      ga4_property_id: p.analytics?.ga4_property_id || '',
      gsc_site_url: p.analytics?.gsc_site_url || (p.analytics ? '' : (p.gsc_property || '')),
    },
    entitlements: {
      seo_blogs: true,
      analytics: (p.entitlements || []).includes('analytics'),
      gmb_audit: (p.entitlements || []).includes('gmb_audit'),
      // Wave 13 (session 46): Off and On only (decision 11)
      newsletter: (p.entitlements || []).includes('newsletter'),
    },
    // Wave 13: pack.newsletter, as the client's onboarding wrote it (or empty)
    newsletter: newsletterFromPack(p.newsletter, { website: p.business?.website || p.website || '' }),
    // Session 23: SEO recommendations, Off / Preview / On
    seo_recommendations: seoRecsMode(p.entitlements),
    // Session 28: AI courses, Off / Preview / On. Session 27 shipped the
    // entitlement with no way to set it, so a course could only be switched
    // on with raw SQL.
    courses: courseMode(p.entitlements),
    blog_tags: tagGroupsToText(p.blog?.tags),
    // Session 16: plan focus + the SEO keywords the planner stays away from
    blog_focus: { focus: ['seo', 'brand', 'mixed'].includes(p.blog?.focus) ? p.blog.focus : 'seo',
                  avoid_keywords: (p.blog?.avoid_keywords || []).join('\n') },
    platform: p.platform || 'other',
    images: imagesFromPack(p.images),   // Session 63/64
    brand: brandFromPack(p.brand, p.newsletter),   // Session 63
    review: { agency_draft_review: p.review?.agency_draft_review === true },
    approval: approvalFromPack(p),   // Session 13
    cadence: {
      // Session 15: type + monthly fields (kept in the form for both types so
      // switching back and forth doesn't lose the picks)
      type: String(cad.type || 'weekly').toLowerCase() === 'monthly' ? 'monthly' : 'weekly',
      month_days: cad.month_days || MONTHLY_DEFAULTS.month_days,
      // Session 15b: a monthly client without a weekday publishes on the exact date ('')
      publish_weekday: String(cad.type || '').toLowerCase() === 'monthly'
        ? (cad.publish_weekday || '') : (cad.publish_weekday || MONTHLY_DEFAULTS.publish_weekday),
      posts_per_date: Number(cad.posts_per_date) || 1,
      kickoff_date: cad.kickoff_date || '',
      publish_weekdays: cad.publish_weekdays || ['tue'],
      questions_lead_days: cad.questions_lead_days ?? 7,
      approve_by_lead_days: cad.approve_by_lead_days ?? 2,
      nudges_enabled: cad.nudges_enabled !== false,
      timezone: cad.timezone || 'America/Los_Angeles',
      publish_time: cad.publish_time || '08:00',
      questions_time: cad.questions_time || '07:00',
      fast_start: cad.fast_start === true,   // Session 55 Part B
    },
  };
}

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const addDaysIso = (iso, n) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

// Session 12 Part A: the entitlement toggles. Analytics switches on by itself
// when the droplet's connection check passes; after that it can be toggled.
const MODULES = [
  ['seo_blogs', 'SEO Blogs', 'Plans, questions, drafts and publishing. Always on.'],
  ['analytics', 'Analytics', 'The Results tab, nightly GA4 and Search Console loads, traffic alerts.'],
  ['gmb_audit', 'GMB Audit', 'Google Business Profile audits you upload and release.'],
  // Wave 13 (session 46): Off or On, nobody gets it automatically (decisions 11, 15)
  ['newsletter', 'Newsletter', 'A branded newsletter PDF for every post that goes out, under Blogs beside the package. The client picks the look in onboarding, or you set it below.'],
];

// Session 49 (Wave 14, pack 14): "ask them to sign up". One email, in Leena's
// voice, with the Learn more link; her mail program opens with it filled in so
// she reads it before it goes. DRAFT copy for her approval. Keyed by the
// catalogue group; the label is what the client sees in their rail.
const ASK_TO_ADD = {
  email: ['Newsletters', 'A newsletter made from every blog post we write for you, in your brand, ready to send to your list.'],
  seo: ['SEO recommendations', 'A quarterly review of how your site reads to Google and the AI crawlers, with a plain-English list of what to fix.'],
  gmb: ['GMB Audit', 'A full review of your Google Business Profile with the specific changes that move you up in the map results.'],
  courses: ['AI Courses', 'Short, practical lessons on using AI in your own work, written for people who are not technical.'],
};
function askToAddHref(client, group, label, line) {
  const to = (client.pack?.people?.notice_emails || [])[0] || '';
  const first = (client.name || '').split(' ')[0];
  const link = `https://portal.digitallotusmarketing.com/learn/${group}`;
  const body = [
    `Hi ${first || 'there'},`,
    '',
    `We have added ${label} to the portal and I thought of you. ${line}`,
    '',
    `You can read how it works and add it to your plan yourself here: ${link}`,
    '',
    'Any questions, just reply to this.',
    '',
    'Leena',
  ].join('\n');
  return `mailto:${to}?subject=${encodeURIComponent(`${label} for ${client.name || 'you'}`)}&body=${encodeURIComponent(body)}`;
}
const ADDON_GROUP_ON = {   // the entitlement that says a group is already on
  email: 'newsletter', seo: 'seo_recommendations', gmb: 'gmb_audit', courses: 'ai_courses',
};

// Session 23 / 28: the SEO recommendations and AI courses switches live in
// components/ServiceBits.jsx since Session 55 Part C (Add client has them too).

// One plain line for pack.analytics.status. Clients set up before Session 12
// have analytics + the entitlement but no status: they are connected.
function analyticsStatus(an, entitled) {
  const st = an?.status;
  if (!an) return { tone: 'none', text: 'Not set up. Add a GA4 property ID or a Search Console property and save.' };
  if (!st) return entitled ? { tone: 'ok', text: 'Connected' } : { tone: 'none', text: 'Saved, not checked yet. Use Check again.' };
  if (st.state === 'pending' || st.state === 'running') return { tone: 'pending', text: 'Checking connection…' };
  if (st.state === 'ok') {
    const first = [st.ga4_first_date, st.gsc_first_date].filter(Boolean).sort()[0];
    return { tone: 'ok', text: first ? `Connected · data from ${fmtDate(first)}` : 'Connected' };
  }
  return { tone: 'err', text: st.message || 'The connection check failed.' };
}

// Session 7: pack.blog.tags = [{label, tags[]}] <-> one group per line,
// "Body area: Shoulder, Knee, Hip". The planner assigns 1 to 3 of these to
// every post; the drafter carries them to the site.
// Session 16: pack.blog.focus (the list lives in ServiceBits.jsx since Session 57)

function tagGroupsToText(groups) {
  return (groups || []).map((g) => `${g.label}: ${(g.tags || []).join(', ')}`).join('\n');
}
function textToTagGroups(text) {
  return String(text || '').split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
    const i = line.indexOf(':');
    const label = i > 0 ? line.slice(0, i).trim() : 'Tags';
    const rest = i > 0 ? line.slice(i + 1) : line;
    return { label, tags: rest.split(',').map((t) => t.trim()).filter(Boolean) };
  }).filter((g) => g.tags.length > 0);
}

function buildPatch(form, init) {
  const patch = {};
  if (form.name !== init.name) patch.name = form.name;
  if (form.publish_adapter !== init.publish_adapter) patch.publish_adapter = form.publish_adapter;
  if (!same(form.business, init.business)) patch.business = { ...form.business, services: form.business.services.filter((s) => s.url.trim()) };
  if (!same(form.market, init.market)) patch.market = { ...form.market,
    competitors: form.market.competitors.filter((c) => c.name.trim() || c.url.trim()), proof: form.market.proof.filter((x) => x.text.trim()) };
  if (!same(form.voice, init.voice)) patch.voice = { tone: form.voice.tone, use_phrases: splitLines(form.voice.use_phrases), banned_phrases: splitLines(form.voice.banned_phrases) };
  if (!same(form.people, init.people)) {
    patch.people = {};
    if (form.people.notice_emails !== init.people.notice_emails) patch.people.notice_emails = splitLines(form.people.notice_emails);
    if (form.people.signin_emails !== init.people.signin_emails) patch.people.signin_emails = splitLines(form.people.signin_emails);
    if (form.people.approver_email !== init.people.approver_email) patch.people.approver_email = form.people.approver_email;
    if (!same(form.people.byline, init.people.byline)) patch.people.byline = form.people.byline;
  }
  if (!same(form.analytics, init.analytics)) {
    patch.analytics = { ga4_property_id: form.analytics.ga4_property_id.trim(), gsc_site_url: form.analytics.gsc_site_url.trim() };
  }
  if (!same(form.entitlements, init.entitlements)) {
    patch.entitlements = MODULES.map(([k]) => k).filter((k) => form.entitlements[k]);
  }
  if (form.seo_recommendations !== init.seo_recommendations) patch.seo_recommendations = form.seo_recommendations;
  if (form.courses !== init.courses) patch.courses = form.courses;
  if (form.blog_tags !== init.blog_tags) patch.blog = { tags: textToTagGroups(form.blog_tags) };
  // Session 16: the blog section also carries focus / avoid_keywords
  if (form.blog_focus.focus !== init.blog_focus.focus) patch.blog = { ...(patch.blog || {}), focus: form.blog_focus.focus };
  if (form.blog_focus.avoid_keywords !== init.blog_focus.avoid_keywords) {
    patch.blog = { ...(patch.blog || {}), avoid_keywords: splitLines(form.blog_focus.avoid_keywords) };
  }
  if (form.platform !== init.platform) patch.platform = form.platform;
  if (!same(form.images, init.images)) {
    patch.images = { max_in_body: Number(form.images.max_in_body), source: form.images.source,
                     look: form.images.look, painter: form.images.painter, notes: form.images.notes };   // Session 63/64
  }
  if (!same(form.brand, init.brand)) patch.brand = brandPayload(form.brand);   // Session 63
  // Wave 13: the newsletter settings (never the transient logo preview URL)
  if (!same(newsletterPayload(form.newsletter), newsletterPayload(init.newsletter))) patch.newsletter = newsletterPayload(form.newsletter);
  if (!same(form.approval, init.approval)) patch.approval = approvalPayload(form.approval);
  if (!same(form.review, init.review)) patch.review = form.review;
  if (!same(cadencePatch(form, init), cadencePatch(init, init))) patch.cadence = cadencePatch(form, init);
  return patch;
}

// Session 15: the cadence section of the patch. Weekly sends exactly the
// fields it always did (plus type 'weekly' when switching back from monthly);
// monthly sends type + month_days + publish_weekday instead of the weekdays.
function cadencePatch(form, init) {
  const { type, month_days: md, publish_weekday: pw, publish_weekdays: pws, posts_per_date: ppd, kickoff_date: kick, ...rest } = form.cadence;
  const common = { ...rest, questions_lead_days: Number(rest.questions_lead_days),
                   approve_by_lead_days: Number(rest.approve_by_lead_days) };
  // Session 15b: '' weekday / kick-off are sent as '' and removed server side
  if (type === 'monthly') return { ...common, type: 'monthly', month_days: [...md].sort((a, b) => a - b), publish_weekday: pw,
                                   posts_per_date: Number(ppd) || 1, kickoff_date: kick || '' };
  return { publish_weekdays: pws, ...common, ...(init.cadence.type === 'monthly' ? { type: 'weekly' } : {}) };
}

function cadenceMoves(form, init) {
  // nudges and the fast-start switch (Session 55 Part B) move no post
  const a = { ...cadencePatch(form, init), nudges_enabled: null, fast_start: null }; const b = { ...cadencePatch(init, init), nudges_enabled: null, fast_start: null };
  return !same(a, b);
}

function EditTab({ client, onSaved }) {
  const init = useMemo(() => initialForm(client), [client]);
  const [form, setForm] = useState(init);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [preview, setPreview] = useState(null);   // {moves, patch}
  const saved = useSaveStatus();   // Session 24
  useEffect(() => { setForm(init); }, [init]);

  const set = (path, value) => setForm((f) => {
    const next = structuredClone(f);
    let o = next; const keys = path.split('.');
    for (const k of keys.slice(0, -1)) o = o[k];
    o[keys[keys.length - 1]] = value;
    return next;
  });
  const patch = buildPatch(form, init);
  const dirty = Object.keys(patch).length > 0;
  const ppw = form.cadence.publish_weekdays.length;

  // Session 12 Part A: watch pack.analytics while a check is pending. Only the
  // status is polled (a full reload would reset unsaved edits); once it
  // settles and nothing is unsaved, reload so the Modules toggles catch up.
  const isDL = client.client_id === 'digitallotus';
  const isDemo = isDemoName(client.name);   // Session 67: "(Demo)" in the name is the marker (lib/demo.js)
  const [liveAn, setLiveAn] = useState(null);
  useEffect(() => { setLiveAn(null); }, [client]);
  const an = liveAn ? liveAn.analytics : client.pack?.analytics;
  const entitledNow = (liveAn ? liveAn.entitlements : client.pack?.entitlements || []).includes('analytics');
  const anStatus = analyticsStatus(an, entitledNow);
  useEffect(() => {
    if (anStatus.tone !== 'pending') return undefined;
    let cancelled = false;
    const t = setInterval(async () => {
      const { data } = await supabase.from('v_portal_client_settings')
        .select('analytics:pack->analytics, entitlements:pack->entitlements')
        .eq('client_id', client.client_id).maybeSingle();
      if (cancelled || !data) return;
      setLiveAn({ analytics: data.analytics, entitlements: data.entitlements || [] });
      const state = data.analytics?.status?.state;
      if (state && state !== 'pending' && state !== 'running' && !dirty) await onSaved();
    }, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [anStatus.tone, client.client_id, dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkAgain() {
    setBusy(true); setBanner(null);
    const { error } = await supabase.rpc('portal_update_client', { p_client_id: client.client_id, p_patch: {
      analytics: { ga4_property_id: init.analytics.ga4_property_id, gsc_site_url: init.analytics.gsc_site_url, recheck: true } } });
    setBusy(false);
    if (error) { setBanner({ tone: 'err', text: error.message }); return; }
    setBanner({ tone: 'ok', text: 'Checking the connection again.' });
    await onSaved();
  }

  async function save(withReschedule) {
    setBusy(true); setBanner(null); saved.start();
    const p = { ...patch };
    if (withReschedule) p.reschedule = true;
    const { data, error } = await supabase.rpc('portal_update_client', { p_client_id: client.client_id, p_patch: p });
    setBusy(false); setPreview(null);
    if (error) { saved.fail(error.message); return; }
    saved.ok();
    if (data !== 'no changes' && data.replace(/^saved\s*/, '').trim()) setBanner({ tone: 'ok', text: data.replace(/^saved\s*/, '').trim() });
    await onSaved();
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!dirty) { setBanner({ tone: 'ok', text: 'Nothing changed.' }); return; }
    if (isDemo) {
      // Session 67: a demo stays a demo (suffix, agency-only contacts,
      // handoff), or a recording could email a real person or publish to a
      // real site.
      if (!isDemoName(form.name)) { setBanner({ tone: 'err', text: 'This is a demo client. Keep "(Demo)" at the end of the name.' }); return; }
      const outside = nonAgencyEmails([...splitLines(form.people.notice_emails), ...splitLines(form.people.signin_emails), form.people.approver_email.trim()]);
      if (outside.length) { setBanner({ tone: 'err', text: `A demo client can only have Digital Lotus contacts (@${AGENCY_DOMAIN}). Not allowed: ${outside.join(', ')}.` }); return; }
      // Session 68b: the sign-in is the login used on the call; it must be its own (plus) address.
      const signins = splitLines(form.people.signin_emails);
      if (!signins.length) { setBanner({ tone: 'err', text: `A demo needs a portal sign-in: the login you use on the call. ${DEMO_SIGNIN_RULE}` }); return; }
      const badSignin = badDemoSignins(signins, session?.user?.email);
      if (badSignin.length) { setBanner({ tone: 'err', text: `${DEMO_SIGNIN_RULE} Not allowed: ${badSignin.join(', ')}.` }); return; }
      if (form.publish_adapter !== 'handoff') { setBanner({ tone: 'err', text: 'A demo client stays on handoff publishing.' }); return; }
      // Session 68: on the call the client login approves the plan and the draft on screen.
      if (form.approval.mode === 'agency_only') { setBanner({ tone: 'err', text: 'A demo cannot be agency-only approval: on the call the client login approves the plan and the draft on screen.' }); return; }
    }
    if (patch.cadence && cadenceMoves(form, init)) {
      setBusy(true); setBanner(null);
      const { data, error } = await supabase.rpc('portal_preview_cadence_change', { p_client_id: client.client_id, p_cadence: patch.cadence });
      setBusy(false);
      if (error) { setBanner({ tone: 'err', text: error.message }); return; }
      setPreview({ moves: (data?.moves || []).filter((m) => m.moves), all: data?.moves || [] });
      return;
    }
    await save(false);
  }

  return (
    <form className="client-form" onSubmit={onSubmit}>
      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}
      {isDemo && <DemoPanel clientId={client.client_id} onReset={onSaved} />}

      <section className="form-section">
        <h2>Contacts</h2>
        <Field label="Notice emails" hint="Who hears about plans, questions and drafts. One per line or comma separated. Changing this never re-sends past emails.">
          <textarea rows={2} value={form.people.notice_emails} onChange={(e) => set('people.notice_emails', e.target.value)} />
        </Field>
        <Field label="Portal sign-in emails" hint="Adding an address sends that person a password setup email. Removing one does not sign them out.">
          <textarea rows={2} value={form.people.signin_emails} onChange={(e) => set('people.signin_emails', e.target.value)} />
        </Field>
        <Field label="Approver email" hint="Leave blank to use the first notice email.">
          <input type="email" value={form.people.approver_email} onChange={(e) => set('people.approver_email', e.target.value)} />
        </Field>
      </section>

      <section className="form-section">
        <h2>Byline</h2>
        <p className="hint" style={{ marginTop: 0 }}>Shown on every post, in the footer bio and in the author schema. Leave the name blank to publish under the business name only.</p>
        <div className="inline-fields">
          <Field label="Name"><input value={form.people.byline.name} onChange={(e) => set('people.byline.name', e.target.value)} placeholder="Dr. Jane Doe, DPT" /></Field>
          <Field label="Title"><input value={form.people.byline.title} onChange={(e) => set('people.byline.title', e.target.value)} placeholder="Founder of Example Co" /></Field>
        </div>
        <Field label="One-line bio"><textarea rows={2} value={form.people.byline.bio} onChange={(e) => set('people.byline.bio', e.target.value)} /></Field>
        <div className="inline-fields">
          <Field label="About page URL"><input type="url" value={form.people.byline.url} onChange={(e) => set('people.byline.url', e.target.value)} placeholder="https://example.com/about" /></Field>
          <Field label="Organisation name for schema" hint="How the business name is written in the author schema."><input value={form.people.byline.org} onChange={(e) => set('people.byline.org', e.target.value)} /></Field>
        </div>
      </section>

      <section className="form-section">
        <h2>Voice and brand</h2>
        <Field label="Business name"><input value={form.name} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Website"><input type="url" value={form.business.website} onChange={(e) => set('business.website', e.target.value)} /></Field>
        <Field label="What they do"><textarea rows={3} value={form.business.description} onChange={(e) => set('business.description', e.target.value)} /></Field>
        <div className="inline-fields">
          <Field label="Service area" hint='Cities or regions, comma separated, or "national".'><input value={form.business.service_area} onChange={(e) => set('business.service_area', e.target.value)} /></Field>
          <Field label="Industry"><input value={form.business.industry} onChange={(e) => set('business.industry', e.target.value)} /></Field>
        </div>
        <Field label="Brand voice notes"><textarea rows={4} value={form.voice.tone} onChange={(e) => set('voice.tone', e.target.value)} /></Field>
        <Field label="Words and phrases to use" hint="One per line."><textarea rows={3} value={form.voice.use_phrases} onChange={(e) => set('voice.use_phrases', e.target.value)} /></Field>
        <Field label="Words and phrases to avoid" hint="One per line."><textarea rows={3} value={form.voice.banned_phrases} onChange={(e) => set('voice.banned_phrases', e.target.value)} /></Field>
        <Field label="Topics to avoid"><textarea rows={2} value={form.market.avoid_topics} onChange={(e) => set('market.avoid_topics', e.target.value)} /></Field>
        <div className="field">
          <span className="field-label">Proof we can use</span>
          <Rows rows={form.market.proof} setRows={(r) => set('market.proof', r)} addLabel="Add proof"
                fields={[['text', 'What it is'], ['url', 'https://… (optional)', 'url']]} />
        </div>
        {/* Session 64: logo, colours and font, asked once; the newsletter and
            the title images both read them. */}
        <h3 className="onb-sub">Logo, colours and font</h3>
        <BrandSetup value={form.brand} onChange={(v) => set('brand', v)} clientId={client.client_id} />
      </section>

      <section className="form-section">
        <h2>Blog focus</h2>
        <p className="hint" style={{ marginTop: 0 }}>What the planner (and Replace) builds this client's posts around. The keywords below are hard exclusions: no title, keyword or slug may target them.</p>
        <div className="field approval-fields" role="radiogroup" aria-label="Blog focus">
          <span className="field-label">Focus</span>
          {BLOG_FOCUS.map(([k, label, hint]) => (
            <label key={k} className="check approval-option">
              <input type="radio" name="blog-focus" value={k} checked={form.blog_focus.focus === k}
                     onChange={() => set('blog_focus.focus', k)} />
              <span>
                <span className="approval-option-label">{label}</span>
                <span className="field-hint">{hint}</span>
              </span>
            </label>
          ))}
        </div>
        <Field label="SEO keywords to avoid" hint="One per line. Use this for search terms someone else covers (for example another writer's SEO blogs).">
          <textarea rows={4} value={form.blog_focus.avoid_keywords} onChange={(e) => set('blog_focus.avoid_keywords', e.target.value)}
                    placeholder={'interior designer near me\ninterior designer cost'} />
        </Field>
      </section>

      <section className="form-section">
        <h2>Competitors and money pages</h2>
        <div className="field">
          <span className="field-label">Competitors (up to 5)</span>
          <Rows rows={form.market.competitors} setRows={(r) => set('market.competitors', r)} addLabel="Add a competitor" max={5}
                fields={[['name', 'Name'], ['url', 'https://competitor.com', 'url']]} />
        </div>
        <div className="field">
          <span className="field-label">Services and money pages</span>
          <span className="field-hint">The pages posts point readers to. Their paths become the money pages and the allowed internal links.</span>
          <Rows rows={form.business.services} setRows={(r) => set('business.services', r)} addLabel="Add a page"
                fields={[['url', 'https://example.com/service', 'url'], ['description', 'One line about this page']]} />
          {(client.pack?.money_pages || []).length > 0 && <span className="field-hint">Current money pages: {(client.pack.money_pages || []).join(', ')}</span>}
        </div>
      </section>

      <section className="form-section">
        <h2>Setup</h2>
        <Field label="Website platform" hint="Squarespace clients get a paste-ready pack in every download. Lovable sites can use the Lovable publish path below; otherwise the platform is informational.">
          <select value={form.platform} onChange={(e) => set('platform', e.target.value)}>
            <option value="squarespace">Squarespace</option>
            <option value="wordpress">WordPress</option>
            <option value="astro">Astro</option>
            <option value="react">React / custom</option>
            <option value="lovable">Lovable (synced with GitHub)</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="How posts go live">
          {form.publish_adapter === 'partner_api' ? (
            <p className="hint" style={{ margin: 0 }}>Automatically through Build A Better Website. Change it in the Publishing tab.</p>
          ) : isDL ? (
            <p className="hint" style={{ margin: 0 }}>Published to the Digital Lotus site from the droplet. It doesn't change here.</p>
          ) : isDemo ? (
            <p className="hint" style={{ margin: 0 }}>Handoff. A demo client never publishes anywhere.</p>
          ) : (
          // Session 12C: no Repo (Digital Lotus only). Handoff and WordPress,
          // plus Lovable only for a client already set to it.
          <select value={form.publish_adapter} onChange={(e) => set('publish_adapter', e.target.value)}>
            <option value="handoff">Handoff: we deliver the ready-to-post package</option>
            <option value="wordpress">WordPress: we publish straight to the site through its own login (handoff until that login is saved on the droplet)</option>
            {client.publish_adapter === 'lovable' && (
              <option value="lovable">Lovable: we commit to the synced GitHub repo, publish, and verify the live URL</option>
            )}
          </select>
          )}
        </Field>
        <Field label="Blog tags" hint="The tag vocabulary for this client's blog, one group per line as Group: tag, tag. Every planned post gets 1 to 3 of these (first group first). Leave empty for no tags.">
          <textarea rows={4} value={form.blog_tags} onChange={(e) => set('blog_tags', e.target.value)} placeholder={'Body area: Shoulder, Knee, Hip\nActivity: Pickleball, Tennis\nTopic: Mobility'} />
        </Field>
        <ApprovalFields
          value={form.approval}
          disabled={isDL}
          lockedNote={isDL ? 'Digital Lotus approves its own posts, so this stays as it is.' : null}
          onChange={(a) => setForm((f) => ({ ...f, approval: a,
            // automatic approval needs the agency review gate on
            review: a.mode === 'client_deadline' ? { ...f.review, agency_draft_review: true } : f.review }))} />
        <Toggle checked={form.review.agency_draft_review} onChange={(v) => set('review.agency_draft_review', v)}
                disabled={form.approval.mode === 'client_deadline'}
                label="Agency draft review"
                hint={form.approval.mode === 'client_deadline'
                  ? 'Always on with automatic approval: you review every plan and draft, and the client\'s clock starts when you send it.'
                  : 'On: every draft and revision comes to you first. The client sees nothing until you send it to them.'} />
        <Toggle checked={form.cadence.nudges_enabled} onChange={(v) => set('cadence.nudges_enabled', v)}
                label="Nudges"
                hint="Reminder emails when a plan, questions or a draft has waited 3 days. Two reminders, then a flag to you." />
        {/* Session 55 Part B */}
        <Toggle checked={form.cadence.fast_start} onChange={(v) => set('cadence.fast_start', v)}
                label="Fast start"
                hint={form.cadence.fast_start
                  ? `For a client onboarded by hand: first post as soon as 7 days out; the first post's questions go out the moment the plan is approved and its draft is written as soon as the answers land${form.approval.mode === 'client_deadline' ? '; the first plan and those first questions approve themselves after 2 days of silence' : form.approval.mode === 'client' ? '; if the client picks "Keep things moving" on their Welcome screen, the first plan and first questions get the 2-day clock' : ''}. Only the first post; the rest runs on the cadence below.`
                  : 'Off: the ordinary 14-day lead and the normal questions timing for every post.'} />
      </section>

      <section className="form-section">
        <h2>Analytics</h2>
        {isDL ? (
          <p className="hint" style={{ marginTop: 0 }}>Digital Lotus analytics are set up on the droplet. {anStatus.text}</p>
        ) : (
          <>
            <p className="hint" style={{ marginTop: 0 }}>Add ops@digitallotusmarketing.com as a Viewer in GA4 and a user in Search Console first. Saving runs a connection check; when it passes, the history loads and the Results tab switches on.</p>
            <Field label="GA4 property ID" hint="The number in GA4 Admin → Property details (not the account ID). Leave blank if there's no GA4.">
              <input inputMode="numeric" value={form.analytics.ga4_property_id} onChange={(e) => set('analytics.ga4_property_id', e.target.value)} placeholder="123456789" />
            </Field>
            <Field label="Search Console property" hint="Exactly as Search Console shows it. Leave blank if there's no Search Console.">
              <input value={form.analytics.gsc_site_url} onChange={(e) => set('analytics.gsc_site_url', e.target.value)} placeholder="sc-domain:example.com" />
            </Field>
            <div className={`analytics-status analytics-status-${anStatus.tone}`} role="status">
              {anStatus.tone === 'pending' && <span className="spinner spinner-sm" aria-hidden="true" />}
              <span>{anStatus.text}</span>
              {(anStatus.tone === 'err' || (an && !an.status && !entitledNow)) && (
                <button type="button" className="btn" onClick={checkAgain} disabled={busy || !same(form.analytics, init.analytics)}
                        title={!same(form.analytics, init.analytics) ? 'Save your changes first; saving runs the check.' : undefined}>
                  Check again
                </button>
              )}
            </div>
          </>
        )}
      </section>

      <section className="form-section">
        <h2>Modules</h2>
        {MODULES.map(([k, label, hint]) => {
          const lockedOn = k === 'seo_blogs';
          const analyticsBlocked = k === 'analytics' && !init.entitlements.analytics && an?.status?.state !== 'ok';
          return (
            <Toggle key={k} checked={form.entitlements[k]} onChange={(v) => set(`entitlements.${k}`, v)}
                    disabled={lockedOn || (analyticsBlocked && !form.entitlements.analytics)} label={label}
                    hint={analyticsBlocked ? 'Analytics can switch on once the connection check passes.' : hint} />
          );
        })}
        {/* Session 49 (Wave 14): what they added themselves, and the nudge for what they lack */}
        {(client.pack?.billing?.addons || []).length > 0 && (
          <div className="field">
            <span className="field-label">Added from inside the portal</span>
            <ul className="hint" style={{ margin: '4px 0 0 18px' }}>
              {(client.pack.billing.addons || []).map((a, i) => (
                <li key={i}>
                  {(a.plan_codes || []).join(', ')} on {String(a.at || '').slice(0, 10)}
                  {a.amount_cents != null ? `, $${(a.amount_cents / 100).toFixed(a.amount_cents % 100 ? 2 : 0)} ${a.bill_interval === 'annual' ? 'a year' : 'a month'}` : ''}
                  {a.via === 'addon_item' ? ', added to their subscription' : ', new subscription'}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="field">
          <span className="field-label">Ask them to sign up</span>
          <span className="hint">
            Opens an email to the client with the Learn more link, for you to read and send. Only for what they do not have.
          </span>
          <div className="review-actions" style={{ justifyContent: 'flex-start', marginTop: 6, flexWrap: 'wrap' }}>
            {Object.entries(ASK_TO_ADD).filter(([g]) => !(client.pack?.entitlements || []).includes(ADDON_GROUP_ON[g])).map(([g, [label, line]]) => (
              <a key={g} className="btn" href={askToAddHref(client, g, label, line)}>Suggest {label}</a>
            ))}
          </div>
        </div>
        {/* Session 23: its own switch, no longer part of SEO Blogs */}
        <div className="field">
          <span className="field-label">SEO recommendations</span>
          {SEO_RECS_MODES.map(([v, label]) => (
            <label key={v} className="check">
              <input type="radio" name="seo-recs-mode" checked={form.seo_recommendations === v}
                     onChange={() => set('seo_recommendations', v)} /> <span>{label}</span>
            </label>
          ))}
          <span className="hint">{SEO_RECS_HINT[form.seo_recommendations]} The client sees a list only after you release it (SEO checklist tab).</span>
        </div>
        {/* Session 28: AI courses, the control Session 27 forgot to build */}
        <div className="field">
          <span className="field-label">AI courses</span>
          {COURSE_MODES.map(([v, label]) => (
            <label key={v} className="check">
              <input type="radio" name="courses-mode" checked={form.courses === v}
                     onChange={() => set('courses', v)} /> <span>{label}</span>
            </label>
          ))}
          <span className="hint">{COURSE_HINT[form.courses]} Everyone signed in for this client gets it; progress is per person.</span>
        </div>
      </section>

      {/* Wave 13 (session 46): the newsletter look. The client sets this in
          onboarding; this is where Leena changes it afterwards (decision 10).
          Shown whenever the module is on, or something is already set. */}
      {(form.entitlements.newsletter || init.newsletter.template) && (
        <section className="form-section">
          <h2>Newsletter</h2>
          <p className="hint" style={{ marginTop: 0 }}>
            Template, call to action and footer for the newsletter made from every post.
            Logo, colours and font come from Voice and brand above. {!form.entitlements.newsletter && 'The module is off, so nothing is built until it is switched on above.'}
          </p>
          <NewsletterSetup
            value={form.newsletter}
            onChange={(v) => set('newsletter', v)}
            business={{ name: form.name, website: form.business.website }}
            clientId={client.client_id}
            brand={form.brand}
            compact
          />
        </section>
      )}

      <section className="form-section">
        <h2>Blog cadence</h2>
        <p className="hint" style={{ marginTop: 0 }}>How often blog posts go out. The newsletter has no schedule of its own: one is made for every post as it is published. Changing the publish days or lead times moves only posts whose questions haven't gone out yet. You'll see the new dates before anything changes.</p>
        <CadenceTypeChoice value={form.cadence.type} onChange={(t) => set('cadence.type', t)} />
        {form.cadence.type === 'monthly' && (
          <MonthlyFields monthDays={form.cadence.month_days} weekday={form.cadence.publish_weekday} weekdays={WEEKDAYS}
                         postsPerDate={form.cadence.posts_per_date} kickoff={form.cadence.kickoff_date}
                         kickoffMin={[addDaysIso(todayIso(), minLeadDays(form.approval?.mode, form.cadence.fast_start)), init.cadence.kickoff_date]
                           .filter(Boolean).sort()[0]}   // a stored kick-off is only re-checked when changed
                         lastWeekday={init.cadence.publish_weekday || MONTHLY_DEFAULTS.publish_weekday}
                         onPostsPerDate={(n) => set('cadence.posts_per_date', n)}
                         onKickoff={(d) => set('cadence.kickoff_date', d)}
                         previewFrom={todayIso()}
                         onDays={(d) => set('cadence.month_days', d)} onWeekday={(w) => set('cadence.publish_weekday', w)} />
        )}
        {form.cadence.type !== 'monthly' && <>
        <Field label="Posts per week">
          <select value={ppw} onChange={(e) => {
            const n = Number(e.target.value);
            const days = form.cadence.publish_weekdays.slice(0, n);
            while (days.length < n) days.push(WEEKDAYS.map(([k]) => k).find((k) => !days.includes(k)));
            set('cadence.publish_weekdays', days);
          }}>
            <option value={1}>1</option><option value={2}>2</option>
          </select>
        </Field>
        <div className="field">
          <span className="field-label">Publish {ppw === 1 ? 'day' : 'days'}</span>
          <div className="inline-fields">
            {form.cadence.publish_weekdays.map((d, i) => (
              <select key={i} value={d} onChange={(e) => set('cadence.publish_weekdays', form.cadence.publish_weekdays.map((x, j) => (j === i ? e.target.value : x)))}>
                {WEEKDAYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            ))}
          </div>
        </div>
        </>}
        <div className="inline-fields">
          <Field label="Questions go out (days before publish)"><input type="number" min={3} max={28} value={form.cadence.questions_lead_days} onChange={(e) => set('cadence.questions_lead_days', e.target.value)} /></Field>
          <Field label="Approve by (days before publish)"><input type="number" min={0} max={14} value={form.cadence.approve_by_lead_days} onChange={(e) => set('cadence.approve_by_lead_days', e.target.value)} /></Field>
          <Field label="Client timezone">
            <select value={form.cadence.timezone} onChange={(e) => set('cadence.timezone', e.target.value)}>
              {TIMEZONES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
        </div>
      </section>

      {/* Session 64: Blog images. Whether we make the title image (Kiva, Iryna,
          Digital Lotus, Dave) or the client gives photos (Cynthia), the look,
          the client's notes for the painter, the painter override, and the
          in-body count. The client answers the first three in onboarding. */}
      <section className="form-section">
        <h2>Blog images</h2>
        <p className="hint" style={{ marginTop: 0 }}>The picture at the top of each post. Title images are drawn in the logo, colours and font under Voice and brand.</p>
        <ImagesSetup value={form.images} onChange={(v) => set('images', v)} brand={form.brand} agency compact />
        <Field label="Photos in the body" hint="How many photo spots a post has below the top picture. They can always be skipped; the top picture is required.">
          <div className="inline-fields" style={{ alignItems: 'center' }}>
            <span>Top picture +</span>
            <input type="number" min={0} max={8} style={{ maxWidth: 90 }} value={form.images.max_in_body} onChange={(e) => set('images.max_in_body', e.target.value)} />
            <span>in the body</span>
          </div>
        </Field>
      </section>

      <div className="review-actions">
        {dirty && <button type="button" className="link-btn" onClick={() => setForm(init)} disabled={busy}>Undo changes</button>}
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        <SaveStatus status={saved.status} />
      </div>

      {preview && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => setPreview(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Move the unsent posts?</h2>
            {preview.moves.length === 0 ? (
              <p>No unsent posts need to move. The new cadence applies to future plans.</p>
            ) : (
              <>
                <p>These {preview.moves.length} unsent post{preview.moves.length === 1 ? '' : 's'} will move to:</p>
                <ul className="moves-list">
                  {preview.moves.map((m) => (
                    <li key={m.job_id}><strong>{m.title || m.slug}</strong><br />
                      <span className="muted">{fmtDate(m.old_publish_date)} → {fmtDate(m.new_publish_date)}</span></li>
                  ))}
                </ul>
                <p className="hint">Posts whose questions already went out keep their dates.</p>
              </>
            )}
            <div className="review-actions">
              <button type="button" className="btn" onClick={() => setPreview(null)} disabled={busy}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={() => save(true)} disabled={busy}>{busy ? 'Saving…' : 'Confirm and save'}</button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
